import React, { useState } from 'react';
import { Tooltip } from '@plerionhq/design-system';
import { TextBlock, useChatStore } from '../../store/chatStore';
import { TokenUsageDisplay } from './TokenUsageDisplay';
import { FeedbackButtons } from './FeedbackButtons';
import { CopyButton } from '../common/CopyButton';
import { SourcesPopup } from './SourcesPopup';

import { useTranslation } from 'react-i18next';

interface MessageActionsProps {
  index: number;
  messageId: string;
  isLastMessage: boolean;
  isReadOnly?: boolean;
  isLoading: boolean;
}

export const useIsPleriDebugMode = () => {
  const [isPleriDebugMode] = useState(
    () => localStorage.getItem('pleri-debug') === 'true',
  );

  return isPleriDebugMode;
};

export const MessageActions: React.FC<MessageActionsProps> = React.memo(
  ({
    index,
    messageId,
    isLastMessage,
    isLoading,
    isReadOnly,
  }: MessageActionsProps) => {
    const { t } = useTranslation('pleri/chat');
    const activeConversationId = useChatStore(
      (state) => state.activeConversationId,
    );
    const message = useChatStore((state) =>
      state.activeConversationId
        ? state.conversations[state.activeConversationId].messages.find(
            (m, i) => m.id === messageId || i === index,
          )
        : null,
    );
    const isPleriDebugMode = useIsPleriDebugMode();

    const regenerateResponse = useChatStore(
      (state) => state.regenerateResponse,
    );

    const handleRegenerate = (messageId: string) => {
      if (activeConversationId) {
        regenerateResponse(activeConversationId, messageId);
      }
    };

    if (!message || !activeConversationId) {
      return null;
    }

    const toolUses =
      message?.content
        .map((content) => (content.type === 'toolUse' ? content : null))
        .filter((toolUse) => toolUse !== null) ?? [];

    // Format duration for display
    const formatDuration = (durationMs?: number) => {
      if (!durationMs) {
        return null;
      }
      const seconds = Math.floor(durationMs / 1000);
      if (seconds < 60) {
        return `${seconds}s`;
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    };

    const durationText = formatDuration(message.duration);

    return (
      <div className="mt-2 flex flex-col gap-2">
        {/* Always show duration and token usage when available - aligned right */}
        {(message.tokenUsage || durationText) && (
          <div className="flex justify-end">
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {durationText && (
                <Tooltip label={t('processingDuration')}>
                  <div className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 dark:border-gray-700 dark:bg-gray-800">
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{durationText}</span>
                  </div>
                </Tooltip>
              )}
              {message.tokenUsage && (
                <TokenUsageDisplay tokenUsage={message.tokenUsage} />
              )}
            </div>
          </div>
        )}
        <div
          className={`flex items-center gap-2 transition-opacity ${
            isLastMessage && !isLoading
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <div className="flex flex-col gap-2">
            {isPleriDebugMode && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {message.id}
              </div>
            )}
            <div className="flex items-center gap-2">
              <CopyButton
                textToCopy={message.content
                  .filter((c): c is TextBlock => c.type === 'text')
                  .map((c) => c.text)
                  .join('\n')}
                tooltipLabel="Copy message to clipboard"
              />

              {!isReadOnly && (
                <FeedbackButtons
                  messageId={messageId}
                  conversationId={activeConversationId}
                />
              )}

              {!isReadOnly && (
                <Tooltip label={t('regenerateResponse')}>
                  <button
                    onClick={() => handleRegenerate(message.id)}
                    className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                    disabled={isLoading}
                  >
                    <svg
                      className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                      />
                    </svg>
                    {t('regenerate')}
                  </button>
                </Tooltip>
              )}

              <SourcesPopup toolUses={toolUses} />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

MessageActions.displayName = 'MessageActions';
